import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

export function RecruiterPageHeader({ section, title, description, action, children, testId }) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-4" data-testid={`${testId}-header`}>
      <div className="min-w-0 flex-1 basis-64 space-y-2">
        <div className="text-xs uppercase font-mono tracking-[0.2em] text-muted-foreground">{section}</div>
        <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight" data-testid={`${testId}-title`}>{title}</h1>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl" data-testid={`${testId}-description`}>{description}</p>
        {children}
      </div>
      {action && <div className="shrink-0 sm:pt-6">{action}</div>}
    </header>
  );
}

export function RecruiterSkeleton({ variant, label }) {
  const board = variant === "pipeline";
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="space-y-6" data-testid={`${variant}-loading`}>
      <span className="sr-only">{label}</span>
      <div aria-hidden="true" className={board ? "grid sm:grid-cols-2 xl:grid-cols-5 gap-4" : variant === "analytics" ? "grid grid-cols-2 lg:grid-cols-4 gap-4" : "space-y-3"}>
        {Array.from({ length: board ? 5 : 4 }, (_, i) => (
          <div key={i} className="rounded-xl border border-border/60 bg-card/60 p-5 space-y-4">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className={board ? "h-28 w-full" : "h-8 w-1/2"} />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ))}
      </div>
      {variant === "analytics" && <Skeleton aria-hidden="true" className="h-72 w-full rounded-xl" />}
    </div>
  );
}
import { ShieldCheck, TriangleAlert, UserCheck, Flag, Sparkles, Inbox, Loader2, RotateCcw } from "lucide-react";

const scoreColor = (s) =>
  s >= 85 ? "text-emerald-400" : s >= 70 ? "text-amber-400" : "text-rose-400";
const barColor = (s) =>
  s >= 85 ? "bg-emerald-500" : s >= 70 ? "bg-amber-500" : "bg-rose-500";

export function TrustBadge({ type, children, className, ...props }) {
  const map = {
    high: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    medium: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    override: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
    flag: "bg-rose-500/15 text-rose-400 border-rose-500/30",
    ai: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  };
  const icons = {
    high: ShieldCheck,
    medium: TriangleAlert,
    override: UserCheck,
    flag: Flag,
    ai: Sparkles,
  };
  const Icon = icons[type] || ShieldCheck;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border font-mono tracking-wide",
        map[type] || map.high,
        className
      )}
      {...props}
    >
      <Icon className="h-3 w-3" />
      {children}
    </span>
  );
}

export function ScoreRing({ score, size = 56, label = "fit" }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max((score ?? 0) / 100, score > 0 ? 0.04 : 0);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth="4" className="stroke-muted/50" fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth="4"
          strokeLinecap="round"
          className={cn(score >= 85 ? "stroke-emerald-500" : score >= 70 ? "stroke-amber-500" : "stroke-rose-500")}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("font-mono text-sm font-bold", scoreColor(score ?? 0))}>{score ?? "—"}</span>
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

export function CompetencyBar({ name, score, weight, description }) {
  return (
    <div className="space-y-1.5" data-testid={`competency-${name.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <span className="font-medium">{name}</span>
          {weight != null && <span className="text-xs text-muted-foreground font-mono">·{weight}%</span>}
        </div>
        <span className={cn("font-mono font-semibold", scoreColor(score))}>{score}</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={cn("h-full rounded-full", barColor(score))} style={{ width: `${Math.max(score, 2)}%`, transition: "width 0.6s ease" }} />
      </div>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, description, action }) {
  return (
    <div className="p-12 text-center rounded-2xl border border-dashed border-border bg-card/30 space-y-3" data-testid="empty-state">
      <div className="mx-auto h-12 w-12 rounded-xl bg-muted flex items-center justify-center">
        <Icon className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      {description && <p className="text-sm text-muted-foreground max-w-sm mx-auto">{description}</p>}
      {action}
    </div>
  );
}

export function LoadingState({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center p-8 gap-3 text-muted-foreground font-mono text-sm" data-testid="loading-state">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div className="p-6 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 space-y-3" data-testid="error-state">
      <p className="text-sm">{message || "Something went wrong."}</p>
      {onRetry && (
        <button onClick={onRetry} className="inline-flex items-center gap-2 text-sm font-medium hover:underline" data-testid="error-retry">
          <RotateCcw className="h-3.5 w-3.5" /> Try again
        </button>
      )}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "text-primary", testId }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        {Icon && <Icon className={cn("h-4 w-4", accent)} />}
      </div>
      <div className="text-3xl font-display font-bold tracking-tight">{value}</div>
    </div>
  );
}
